import LocalizedLink from './LocalizedLink';
import { trackEvent } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Sparkles, Calendar } from 'lucide-react';
import SocialMediaLinks from './SocialMediaLinks';
import { openCookiePreferences } from '@/lib/cookie-consent';
import BookingLink, { BookingText } from '@/components/BookingLink';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const services = [
    { labelKey: 'AI Websites', href: '/services/ai-websites' },
    { labelKey: 'AI Chatbots & Lead Capture', href: '/services/ai-chatbots' },
    { labelKey: 'CRM Automation', href: '/services/crm-automation' },
    { labelKey: 'Workflow Automation', href: '/services/workflow-automation' },
    { labelKey: 'AI Voice Agents', href: '/services/ai-voice-agents' },
    { labelKey: 'Custom AI Agents', href: '/services/ai-agents' },
    { labelKey: 'AI Audit & Strategy', href: '/services/ai-audit' },
  ];

  const company = [
    { labelKey: 'footer.links.about', href: '/about' },
    { labelKey: 'Partner Program', href: '/partners' },
    { labelKey: 'footer.links.careers', href: '/careers' },
    { labelKey: 'footer.links.contact', href: '/contact' },
    { labelKey: 'footer.links.faq', href: '/faq' },
  ];

  const resources = [
    { labelKey: 'footer.links.aiReadiness', href: '/ai-readiness' },
    { labelKey: 'ROI Calculator', href: '/roi-calculator' },
    { labelKey: 'Funding & Grants', href: '/cdap' },
    { labelKey: 'footer.links.products', href: '/products' },
    { labelKey: 'Client Work', href: '/work' },
    { labelKey: 'footer.links.caseStudies', href: '/case-studies' },
    { labelKey: 'footer.links.blog', href: '/blog' },
    { labelKey: 'footer.links.pricing', href: '/pricing' },
    { labelKey: 'Marketing Packages', href: '/marketing-packages' },
  ];

  const serviceAreas = [
    { labelKey: 'AI Consulting Canada', href: '/ai-consulting-canada' },
    { labelKey: 'SEO Edmonton', href: '/seo-edmonton' },
    { labelKey: 'AI Consulting Edmonton', href: '/ai-consulting-edmonton' },
    { labelKey: 'AI Services Calgary', href: '/ai-services-calgary' },
    { labelKey: 'AI Consulting Vancouver', href: '/ai-consulting-vancouver' },
    { labelKey: 'Web Design Langley', href: '/web-design-langley' },
  ];

  const legal = [
    { labelKey: 'footer.links.privacyPolicy', href: '/privacy-policy' },
    { labelKey: 'footer.links.termsOfService', href: '/terms-of-service' },
    { labelKey: 'footer.links.accessibility', href: '/accessibility' },
  ];

  return (
    <footer className="bg-background border-t border-border">
      {/* CTA Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-cyan-500/10 to-primary/20"></div>
        <div className="container-width py-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="quantum-badge mb-4 mx-auto w-fit">
              <Sparkles className="h-4 w-4 text-primary" />
              {t('footer.getStartedBadge')}
            </p>
            <h3 className="heading-md text-foreground mb-4">
              {t('footer.ctaHeading')}
            </h3>
            <p className="text-foreground/85 mb-6 text-lg">
              {t('footer.ctaBody')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <BookingLink
                location="footer-cta"
                className="shiny-cta"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'Book a Free Consultation',
                    cta_location: 'footer-cta',
                  })
                }
              >
                <span>
                  <Calendar className="h-5 w-5" aria-hidden="true" />
                  <BookingText />
                </span>
              </BookingLink>
            </div>
          </div>
        </div>
      </div>

      <div className="container-width section-padding">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
          {/* Company Info */}
          <div className="col-span-2">
            <LocalizedLink to="/" className="flex items-center gap-2 mb-6 group">
              <img
                src="/brainfy-lockup.png"
                alt="Brainfy"
                width={97}
                height={36}
                className="h-9 w-auto"
              />
            </LocalizedLink>
            <p className="text-foreground/80 mb-6 leading-relaxed text-sm">
              {t('footer.brandBlurb')}
            </p>

            <SocialMediaLinks
              variant="footer"
              size="md"
              className="mb-6"
            />
          </div>

          {/* Services */}
          <div>
            <h3 className="font-medium text-foreground mb-4 text-sm uppercase tracking-wider">
              {t('footer.sectionTitles.services')}
            </h3>
            <ul className="space-y-3">
              {services.map((item) => (
                <li key={item.labelKey}>
                  <LocalizedLink
                    to={item.href}
                    className="text-foreground/80 hover:text-foreground transition-colors text-sm"
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>

            <h3 className="font-medium text-foreground mt-8 mb-4 text-sm uppercase tracking-wider">
              {t('footer.sectionTitles.serviceAreas')}
            </h3>
            <ul className="space-y-3">
              {serviceAreas.map((item) => (
                <li key={item.href}>
                  <LocalizedLink
                    to={item.href}
                    className="text-foreground/80 hover:text-foreground transition-colors text-sm"
                  >
                    {item.labelKey}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-medium text-foreground mb-4 text-sm uppercase tracking-wider">
              {t('footer.sectionTitles.company')}
            </h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.labelKey}>
                  <LocalizedLink
                    to={item.href}
                    className="text-foreground/80 hover:text-foreground transition-colors text-sm"
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-medium text-foreground mb-4 text-sm uppercase tracking-wider">
              {t('footer.sectionTitles.resources')}
            </h3>
            <ul className="space-y-3">
              {resources.map((item) => (
                <li key={item.labelKey}>
                  <LocalizedLink
                    to={item.href}
                    className="text-foreground/80 hover:text-foreground transition-colors text-sm"
                  >
                    {t(item.labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="font-medium text-foreground mb-4 text-sm uppercase tracking-wider">
              {t('footer.sectionTitles.connect')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Phone className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <a href="tel:+17803942451" className="text-foreground/80 hover:text-foreground transition-colors text-sm">
                  780-394-2451
                </a>
              </div>
              <div className="flex items-start space-x-2 min-w-0">
                <Mail className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <a href="mailto:support@brainfyai.com" className="text-foreground/80 hover:text-foreground transition-colors text-sm break-all">
                  support@brainfyai.com
                </a>
              </div>
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground/80 text-sm">
                  {t('footer.locationLabel')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-foreground/80 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-foreground/80 text-sm">
              {legal.map((item) => (
                <LocalizedLink
                  key={item.labelKey}
                  to={item.href}
                  className="hover:text-foreground transition-colors"
                >
                  {t(item.labelKey)}
                </LocalizedLink>
              ))}
              <button
                type="button"
                onClick={openCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                {t('footer.cookieSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
